// ---------------------------------------------------------------------------
// Digital Notice Board — ProjectionView
// ---------------------------------------------------------------------------
// The output side: a black, chrome-free full-screen player that shows ONLY the
// designed screen (letter-boxed to the display's shape) and auto-advances slides
// on their configured durations, looping forever. This is what you point at a TV
// / LED wall. Uses the Fullscreen API when available. Keyboard: Esc closes,
// ←/→ steps slides, Space pauses. Renders through the same ElementVisual as the
// editor so the projection is pixel-identical to the design.
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Flex, HStack, IconButton, Text} from "@chakra-ui/react";
import {FaExpand, FaPause, FaPlay, FaStepBackward, FaStepForward, FaSyncAlt, FaTimes} from "react-icons/fa";
import ElementVisual from "./ElementVisual";
import MarqueeStyle from "./MarqueeStyle";
import {getBoard} from "../services/NoticeBoardService";

export default function ProjectionView({board, startIndex = 0, onClose}) {
    // Keep a LIVE copy so a TV that's been left projecting can pull the newest
    // saved version on demand (Refresh) without anyone touching the display.
    const [liveBoard, setLiveBoard] = useState(board);
    const {canvas, slides} = liveBoard;
    const [index, setIndex] = useState(Math.min(startIndex, slides.length - 1));
    const [paused, setPaused] = useState(false);
    const [scale, setScale] = useState(0.2);
    const [showControls, setShowControls] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const rootRef = useRef(null);
    const hideTimer = useRef(null);

    // If the parent hands over a different/edited board, adopt it.
    useEffect(() => {
        setLiveBoard(board);
    }, [board]);

    // Reload the latest saved content from the store and restart from slide 1.
    const refresh = useCallback(() => {
        const fresh = getBoard(liveBoard.id).data;
        if (fresh) {
            setLiveBoard(fresh);
            setIndex(0);
        }
        setRefreshing(true);
        setTimeout(() => setRefreshing(false), 600);
    }, [liveBoard.id]);

    // Fit the logical canvas inside the viewport, preserving aspect (letterbox).
    useEffect(() => {
        const fit = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            setScale(Math.min(vw / canvas.width, vh / canvas.height));
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, [canvas.width, canvas.height]);

    // Auto-advance on each slide's duration.
    useEffect(() => {
        if (paused || slides.length <= 1) return undefined;
        const secs = slides[index] && slides[index].duration ? slides[index].duration : 8;
        const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), secs * 1000);
        return () => clearTimeout(t);
    }, [index, paused, slides]);

    const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), [slides.length]);
    const prev = useCallback(
        () => setIndex((i) => (i - 1 + slides.length) % slides.length),
        [slides.length]
    );

    // Keyboard controls.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === " ") {
                e.preventDefault();
                setPaused((p) => !p);
            } else if (e.key.toLowerCase() === "r") {
                refresh();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev, onClose, refresh]);

    const goFullscreen = () => {
        const el = rootRef.current;
        if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    };

    // Auto-hide the control bar after a moment of no mouse movement.
    const nudgeControls = () => {
        setShowControls(true);
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setShowControls(false), 2500);
    };
    useEffect(() => {
        nudgeControls();
        return () => hideTimer.current && clearTimeout(hideTimer.current);
    }, []);

    const slide = slides[index];
    const stageW = canvas.width * scale;
    const stageH = canvas.height * scale;

    return (
        <Box
            ref={rootRef}
            position="fixed"
            top="0"
            left="0"
            w="100vw"
            h="100vh"
            bg="black"
            zIndex={3000}
            onMouseMove={nudgeControls}
        >
            <MarqueeStyle />
            <Flex w="100%" h="100%" align="center" justify="center">
                <Box position="relative" w={`${stageW}px`} h={`${stageH}px`} bg={slide.background} overflow="hidden">
                    {slide.elements.map((el) => (
                        <Box
                            key={el.id}
                            position="absolute"
                            left={`${el.x * scale}px`}
                            top={`${el.y * scale}px`}
                            w={`${el.w * scale}px`}
                            h={`${el.h * scale}px`}
                            transform={el.rotation ? `rotate(${el.rotation}deg)` : undefined}
                        >
                            <ElementVisual element={el} scale={scale} />
                        </Box>
                    ))}
                </Box>
            </Flex>

            {/* Control bar */}
            <Flex
                position="absolute"
                bottom="0"
                left="0"
                w="100%"
                p="16px"
                justify="center"
                opacity={showControls ? 1 : 0}
                transition="opacity 0.3s"
                pointerEvents={showControls ? "auto" : "none"}
            >
                <HStack bg="rgba(0,0,0,0.6)" px="16px" py="8px" borderRadius="full" spacing="10px">
                    <IconButton size="sm" variant="ghost" color="white" aria-label="Prev" icon={<FaStepBackward />} onClick={prev} />
                    <IconButton
                        size="sm"
                        variant="ghost"
                        color="white"
                        aria-label="Play/Pause"
                        icon={paused ? <FaPlay /> : <FaPause />}
                        onClick={() => setPaused((p) => !p)}
                    />
                    <IconButton size="sm" variant="ghost" color="white" aria-label="Next" icon={<FaStepForward />} onClick={next} />
                    <Text color="whiteAlpha.800" fontSize="sm" minW="70px" textAlign="center">
                        {index + 1} / {slides.length}
                    </Text>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        color="white"
                        aria-label="Refresh content"
                        title="Reload the latest saved content (R)"
                        icon={<FaSyncAlt className={refreshing ? "nb-spin" : undefined} />}
                        onClick={refresh}
                    />
                    <IconButton size="sm" variant="ghost" color="white" aria-label="Fullscreen" icon={<FaExpand />} onClick={goFullscreen} />
                    <IconButton size="sm" variant="ghost" colorScheme="red" color="red.300" aria-label="Close" icon={<FaTimes />} onClick={onClose} />
                </HStack>
            </Flex>
        </Box>
    );
}
